import { supabase } from '../supabase';
import { invoiceAnchorYear } from '../utils/invoice/invoicePeriodAnchor';
import { fetchAllRows } from '../utils/async/fetchAllRows';
import { parseDbInstant, toTenantDateStr, tenantMidnightUtcIso } from '../utils/date/tenantDate';
import { INVOICE_LIST_COLS } from './invoiceReconcileReads';

/**
 * Years to offer in the invoice year picker.
 *
 * Derived from the period-END anchor (`invoiceAnchorYear`), NOT `date_from`.
 * Using date_from offered a year for every invoice that merely STARTED in it —
 * so a Dec 2025 → Jan 2026 invoice produced a "2025" option that always
 * rendered an empty list, because the list files that invoice under 2026 (the
 * year it bills through). The picker and the list now share one definition.
 *
 * Paged via fetchAllRows so PostgREST's 1000-row cap cannot hide an old year (KAN-1039).
 *
 * Split out of invoiceService.js to keep that file under the 500-line cap;
 * re-exported from there so existing import paths keep working.
 */
export const getDistinctInvoiceYears = async (tenantId) => {
  if (!tenantId) return { data: [], error: null };
  const res = await fetchAllRows((opts) =>
    supabase
      .from('invoices')
      .select('id, date_from, date_to', opts)
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)
      .order('id', { ascending: true }),
  );
  if (res.error) return { data: [], error: res.error };
  const years = [...new Set(
    (res.data || []).map(invoiceAnchorYear).filter((y) => y != null),
  )].sort((a, b) => b - a);
  return { data: years, error: null };
};

/**
 * Years that have PAID invoices, for the Reports year picker (KAN-822).
 *
 * Reports bucket a paid invoice by its `created_at` in the TENANT calendar
 * (`getReportInvoiceDate`), so the year is derived the same way here — not from
 * the invoice period. Selects only `id, created_at` (paged via fetchAllRows so
 * PostgREST's 1000-row cap cannot hide an old year). Tenant scoping is the
 * caller's session tenant + RLS.
 */
// Pages every paid invoice (light columns); a DISTINCT-years RPC would be lighter at scale.
export const getPaidInvoiceYears = async (tenantId, tz) => {
  if (!tenantId) return { data: [], error: null };
  const res = await fetchAllRows((opts) =>
    supabase
      .from('invoices')
      .select('id, created_at, date_to', opts)
      .eq('tenant_id', tenantId)
      .eq('status', 'paid')
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true }),
  );
  if (res.error) return { data: [], error: res.error };
  const years = new Set();
  for (const row of res.data || []) {
    // Mirror getReportInvoiceDate: created_at in the tenant tz, else the date_to year.
    const ymd = row?.created_at ? toTenantDateStr(parseDbInstant(row.created_at), tz) : String(row?.date_to || '');
    const y = Number(ymd.slice(0, 4));
    if (Number.isInteger(y) && y > 1900) years.add(y);
  }
  return { data: [...years].sort((a, b) => b - a), error: null };
};

/**
 * PAID invoices Reports files under tenant-calendar `year` (KAN-822).
 *
 * Reports bucket by `created_at` in the tenant tz (`getReportInvoiceDate`),
 * which is NOT the period-overlap rule `getInvoices({ year })` uses — a paid
 * invoice created Jan 2026 for a Dec 2025 period is missing from the overlap
 * read. Bounds = tenant-local [Jan 1, next Jan 1) as UTC instants; a row with
 * no `created_at` falls back to `date_to` in that year (same as the report).
 * Same select shape as getInvoices so merged rows are complete for every consumer.
 */
export const getPaidInvoicesCreatedInYear = async (tenantId, tz, year) => {
  if (!tenantId || !Number.isInteger(year)) return { data: [], error: null };
  const start = tenantMidnightUtcIso(`${year}-01-01`, tz);
  const end = tenantMidnightUtcIso(`${year + 1}-01-01`, tz);
  const nullCreated = `and(created_at.is.null,date_to.gte.${year}-01-01,date_to.lte.${year}-12-31)`;
  const res = await fetchAllRows((opts) =>
    supabase
      .from('invoices')
      .select(INVOICE_LIST_COLS, opts)
      .eq('tenant_id', tenantId)
      .eq('status', 'paid')
      .is('deleted_at', null)
      .or(`and(created_at.gte.${start},created_at.lt.${end}),${nullCreated}`)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true }),
  );
  return { data: res.data, error: res.error };
};
